export const MIN_COLUMNS = 1;
export const MAX_COLUMNS = 12;

export const DEFAULT_KPI_COLUMNS = 4;
export const DEFAULT_TABLE_COLUMNS = 2;

export const MIN_TABLE_HEIGHT = 220;
export const MAX_TABLE_HEIGHT = 720;
export const DEFAULT_TABLE_HEIGHT = 360;
export const TABLE_HEIGHT_STEP = 20;

export const DASHBOARD_PAGES = ['wh', 'stp', 'mt5', 'mt4'];

const LEGACY_KPI_STORAGE_KEY = 'godo-dashboard-kpi-columns';
const LEGACY_TABLE_STORAGE_KEY = 'godo-dashboard-table-columns';

function kpiStorageKey(page) {
  return `godo-dashboard-${page}-kpi-columns`;
}

function tableStorageKey(page) {
  return `godo-dashboard-${page}-table-columns`;
}

function tableHeightStorageKey(page) {
  return `godo-dashboard-${page}-table-height`;
}

export function pageFromStorageKey(storageKey) {
  if (!storageKey) return 'wh';
  const page = String(storageKey).split('-')[0];
  return DASHBOARD_PAGES.includes(page) ? page : 'wh';
}

export function clampColumns(value, fallback = DEFAULT_KPI_COLUMNS) {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, parsed));
}

export function loadColumnCount(storageKey, fallback) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null) return fallback;
    return clampColumns(raw, fallback);
  } catch {
    return fallback;
  }
}

function loadStoredNumber(storageKey, fallback, clampFn) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null) return fallback;
    return clampFn(raw, fallback);
  } catch {
    return fallback;
  }
}

export function saveColumnCount(storageKey, columns) {
  try {
    localStorage.setItem(storageKey, String(columns));
  } catch {
    /* ignore */
  }
}

export function clampTableHeight(value, fallback = DEFAULT_TABLE_HEIGHT) {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(MAX_TABLE_HEIGHT, Math.max(MIN_TABLE_HEIGHT, parsed));
}

export function loadKpiColumns(page = 'wh') {
  const perPage = loadColumnCount(kpiStorageKey(page), null);
  if (perPage != null) return perPage;
  return loadColumnCount(LEGACY_KPI_STORAGE_KEY, DEFAULT_KPI_COLUMNS);
}

export function loadTableColumns(page = 'wh') {
  const perPage = loadColumnCount(tableStorageKey(page), null);
  if (perPage != null) return perPage;
  return loadColumnCount(LEGACY_TABLE_STORAGE_KEY, DEFAULT_TABLE_COLUMNS);
}

export function saveKpiColumns(page, columns) {
  saveColumnCount(kpiStorageKey(page), columns);
}

export function saveTableColumns(page, columns) {
  saveColumnCount(tableStorageKey(page), columns);
}

export function loadTableHeight(page = 'wh') {
  try {
    const raw = localStorage.getItem(tableHeightStorageKey(page));
    if (raw != null) {
      const parsed = Number.parseInt(String(raw), 10);
      if (Number.isFinite(parsed) && parsed < MIN_TABLE_HEIGHT) {
        saveTableHeight(page, DEFAULT_TABLE_HEIGHT);
        return DEFAULT_TABLE_HEIGHT;
      }
    }
  } catch {
    /* ignore */
  }

  return loadStoredNumber(
    tableHeightStorageKey(page),
    DEFAULT_TABLE_HEIGHT,
    clampTableHeight
  );
}

export function saveTableHeight(page, height) {
  saveColumnCount(tableHeightStorageKey(page), clampTableHeight(height));
}

export function loadPageLayout(page) {
  return {
    kpiColumns: loadKpiColumns(page),
    tableColumns: loadTableColumns(page),
    tableHeight: loadTableHeight(page),
  };
}

export function loadAllPageLayouts() {
  return DASHBOARD_PAGES.reduce((acc, page) => {
    acc[page] = loadPageLayout(page);
    return acc;
  }, {});
}

export function densityClassForColumns(columns) {
  if (columns >= 7) return 'sortable-card-grid--dense-xl';
  if (columns >= 5) return 'sortable-card-grid--dense-lg';
  if (columns >= 3) return 'sortable-card-grid--dense-md';
  return 'sortable-card-grid--dense-sm';
}
